import type {
  MultichainAccountBalanceResponse,
  MultichainAccountBalancesRequest,
  MultichainAccountBalancesRequestBalanceRequests,
} from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { createApiClient } from '../../apiClient';
import type { Chain } from '../../chain/chain.types';

export type GetMultichainBalancesBalanceRequest =
  Omit<MultichainAccountBalancesRequest, 'balanceRequests'> & {
    balanceRequests: Array<
      Omit<MultichainAccountBalancesRequestBalanceRequests, 'chain'> & {
        chain: Chain;
      }
    >;
  };

export type GetMultichainBalancesParams = {
  balanceRequest: GetMultichainBalancesBalanceRequest;
};

/**
 * Retrieves token balances across multiple blockchain networks.
 *
 * This function fetches cryptocurrency and token balances for the user's
 * wallet addresses across different chains, networks and addresses specified in the request.
 *
 * @param params.balanceRequest - The balance request configuration specifying which chains, networks and addresses to query.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the chain balances across multiple networks.
 */
export const getMultichainBalances = async (
  { balanceRequest }: GetMultichainBalancesParams,
  client = getDefaultClient()
): Promise<MultichainAccountBalanceResponse['chainBalances']> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  const response = await apiClient.postMultichainAccountBalances({
    environmentId: core.environmentId,
    multichainAccountBalancesRequest:
      balanceRequest as MultichainAccountBalancesRequest,
  });

  return response.chainBalances;
};
